import { t } from "i18next"
import * as Yup from "yup"

export const deviceValidationSchema = Yup.object().shape({
  deviceId: Yup.string().required(t("global.regnr_is_required", "Registration number is required")),
  internalDeviceId: Yup.string().required(t("global.internnr_is_required", "Internal number is required")),
  deviceDescription: Yup.string()
    .nullable()
    .max(100, t("global.description_can_only_be_100", "Description has a maximum limit of 100 characters")),
  equipments: Yup.array()
    .nullable()
    .test("priority-checks", function (equipments) {
      const mountedEquipments = (equipments || []).filter(
        (equipment) => equipment && equipment.mountedOnDeviceId !== null
      )

      if (mountedEquipments.length === 0) {
        return true
      }

      const hasPositionPriority = mountedEquipments.some((equipment) => !!equipment.positionPriority)
      const hasIoPriority = mountedEquipments.some((equipment) => !!equipment.ioPriority)

      if (hasPositionPriority && hasIoPriority) {
        return true
      }

      if (!hasPositionPriority && !hasIoPriority) {
        return this.createError({
          message: t(
            "sysadmin.position_and_io_priority_required",
            "Select one position priority and one IO priority"
          )
        })
      }

      if (!hasPositionPriority) {
        return this.createError({
          message: t("sysadmin.position_priority_required", "Select one position priority")
        })
      }

      return this.createError({
        message: t("sysadmin.io_priority_required", "Select one IO priority")
      })
    })
})

export const equipmentValidationSchema = Yup.object().shape(
  {
    companyId: Yup.number()
      .typeError(t("global.company_is_required", "Company is required"))
      .min(1, t("global.company_is_required", "Company is required"))
      .required(t("global.company_is_required", "Company is required")),
    equipmentId: Yup.string().required(t("global.id_is_required", "ID is required")),
    status: Yup.string().required(t("global.status_is_required", "Status is required")),
    externalDeviceTypeId: Yup.string().required(
      t("global.equipment_type_is_required", "Equipment type is required")
    ),
    subscriptionTime: Yup.date().nullable(),
    mountedOnDeviceId: Yup.number()
      .nullable()
      .when("mountedOnStorageId", {
        is: (value) => value === null,
        then: (schema) =>
          schema.required(
            t("reports.connection_to_warehouse_or_unit", "Connection to warehouse or unit is required")
          )
      }),
    mountedOnStorageId: Yup.number()
      .nullable()
      .when("mountedOnDeviceId", {
        is: (value) => value === null,
        then: (schema) =>
          schema.required(
            t("reports.connection_to_warehouse_or_unit", "Connection to warehouse or unit is required")
          )
      }),
    simCardSupplier: Yup.number().nullable(),
    avlId: Yup.number()
      .nullable()
      .max(85000, t("reports.you_can_enter_85000", "You can enter a maximum of 85000"))
  },
  [["mountedOnDeviceId", "mountedOnStorageId"]]
)

export const equipmentToDeviceValidationSchema = Yup.object().shape(
  {
    companyId: Yup.number()
      .typeError(t("global.company_is_required", "Company is required"))
      .min(1, t("global.company_is_required", "Company is required"))
      .required(t("global.company_is_required", "Company is required")),
    equipmentId: Yup.string().required(t("global.id_is_required", "ID is required")),
    status: Yup.string().required(t("global.status_is_required", "Status is required")),
    externalDeviceTypeId: Yup.string().required(
      t("global.equipment_type_is_required", "Equipment type is required")
    ),
    subscriptionTime: Yup.date().nullable(),
    mountedOnDeviceId: Yup.number().nullable(),
    mountedOnStorageId: Yup.number().nullable(),
    simCardSupplier: Yup.number().nullable(),
    avlId: Yup.number()
      .nullable()
      .max(85000, t("reports.you_can_enter_85000", "You can enter a maximum of 85000"))
  },
  [["mountedOnDeviceId", "mountedOnStorageId"]]
)

export const storageValidationSchema = Yup.object().shape({
  companyId: Yup.number()
    .typeError(t("global.company_is_required", "Company is required"))
    .required(t("global.company_is_required", "Company is required")),
  name: Yup.string()
    .required(t("global.name_is_required", "Name is required"))
    .max(50, t("global.name_can_only_be_50", "Name has a maximum limit of 50 characters")),
  maximumCapacity: Yup.number()
    .required(t("reports.enter_at_least_0_max_quantity_in_stock", "Enter at least 0 max quantity in stock"))
    .min(0),
  storageDescription: Yup.string()
    .nullable()
    .max(100, t("global.description_can_only_be_100", "Description has a maximum limit of 100 characters")),
  adress: Yup.string()
    .nullable()
    .max(100, t("reports.adress_has_a_max_limit_on_100", "Address has a maximum limit of 100 characters"))
})
